<template>
  <!-- Contenedor del contador dentro del aro -->
  <div class="countdown-container" @click="handleContainerClick" :key="uiUpdateKey">
    <!-- ESTADO 1: Aún no iniciado => solo PLAY (gris), centrado -->
    <template v-if="!hasStarted && !isFinished && !actionCompleted">
      <button class="icon-btn" @click.stop="start" aria-label="Play">
        <i class="i-andy-video-1 i-andy-m gray-icon"></i>
      </button>
    </template>

    <!-- ESTADO 2: Corriendo => solo PAUSE, centrado -->
    <template v-else-if="isRunning && !isFinished && !actionCompleted">
      <button class="icon-btn" @click.stop="pause" aria-label="Pause">
        <i class="i-andy-pause i-andy-m pause-icon"></i>
      </button>
    </template>

    <!-- ESTADO 3: Pausado (no terminado) => PLAY (verde) + STOP (rojo), lado a lado y centrados -->
    <template v-else-if="!isRunning && hasStarted && !isFinished && !actionCompleted">
      <div class="icons-row">
        <button class="icon-btn circle-bg circle-bg--resume" @click.stop="start" aria-label="Resume">
          <i class="i-andy-video-1 i-andy-m-circle resume-icon"></i>
        </button>
        <button class="icon-btn circle-bg circle-bg--stop" @click.stop="stop" aria-label="Stop & Reset">
          <i class="i-andy-stop i-andy-m-circle stop-icon"></i>
        </button>
      </div>
    </template>

    <!-- ESTADO 4: Terminado => Mostrar tiempo completado y emitir que necesita acción -->
    <template v-else-if="isFinished && !actionCompleted">
      <!-- Mostrar el tiempo completado -->
      <span class="finished-text">
        <span class="hm finished">{{ finishedHmText }}</span>
        <span class="sec finished">{{ finishedSecText }}</span>
      </span>
    </template>

    <!-- ESTADO 5: Acción completada => mostrar botón Play listo para nuevo ciclo -->
    <template v-else-if="actionCompleted">
      <button class="icon-btn" @click.stop="start" aria-label="Start New Cycle">
        <i class="i-andy-video-1 i-andy-m restart-icon"></i>
      </button>
      <!-- <span class="completed-text">Completado</span> -->
    </template>

    <!-- Tiempo en el centro del aro (se muestra siempre que no esté "terminado") -->
    <span v-if="!isFinished" class="time-text">
      <span class="hm" :class="{ pause: isRunning, stop: hasStarted && !isRunning }">{{ hmText }}</span>
      <span class="sec" :class="{ pause: isRunning, stop: hasStarted && !isRunning }">{{ secText }}</span>
    </span>
  </div>
</template>

<script>
import { timerSyncService } from '@/services/timerSyncService'

export default {
  name: 'CountDownManual',
  props: {
    minutes: { type: [Number, String], required: true },
    timerId: { type: String, required: true }
  },
  data() {
    return {
      isRunning: false,
      hasStarted: false,
      isFinished: false,
      actionCompleted: false,
      remainingSec: 0,
      intervalId: null,
      startTime: null,
      pausedTime: null,
      totalElapsed: 0,
      uiUpdateKey: 0,
      autoSaveInterval: null
    }
  },
  computed: {
    initialSec() {
      const mins = Number(this.minutes || 0)
      return Math.max(0, Math.floor(mins * 60))
    },
    hmText() {
      const s = Math.max(0, Number(this.remainingSec || 0))
      const h = Math.floor(s / 3600)
      const m = Math.floor((s % 3600) / 60)
      if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      return String(m).padStart(2, '0')
    },
    secText() {
      const s = Math.max(0, Number(this.remainingSec || 0)) % 60
      return `:${String(s).padStart(2, '0')}`
    },
    finishedHmText() {
      const totalMins = Math.floor(this.initialSec / 60)
      const h = Math.floor(totalMins / 60)
      const m = totalMins % 60
      if (h > 0) return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
      return String(m).padStart(2, '0')
    },
    finishedSecText() {
      return ':00'
    }
  },
  mounted() {
    this.loadSavedState()
    this.autoSaveInterval = setInterval(() => {
      if (this.hasStarted && !this.actionCompleted) {
        this.forceSaveState()
      }
    }, 30000)
  },
  beforeDestroy() {
    this.clear()
    clearInterval(this.autoSaveInterval)
    if ((this.hasStarted && !this.isRunning) || this.isFinished || this.actionCompleted) {
      this.saveState()
    }
  },
  methods: {
    async loadSavedState() {

      const localState = timerSyncService.loadFromLocalStorage(this.timerId)
      if (localState) {
        this.applyState(localState)

        if (localState.isRunning && Number(localState.remainingSec) > 0) {
          this.startFromSavedState()
        } else if (!localState.isRunning && localState.hasStarted && !localState.isFinished &&
                  !localState.actionCompleted && Number(localState.remainingSec) > 0) {
        } else if (localState.isFinished && !localState.actionCompleted) {
          this.$emit('needsAction', true)
        }
        return
      }

      if (navigator.onLine) {
        try {
          const serverState = await timerSyncService.loadInitialState(this.timerId)
          if (serverState) {
            this.applyState(serverState)

            if (serverState.isRunning && Number(serverState.remainingSec) > 0) {
              this.startFromSavedState()
            } else if (!serverState.isRunning && serverState.hasStarted && !serverState.isFinished &&
                      !serverState.actionCompleted && Number(serverState.remainingSec) > 0) {
            } else if (serverState.isFinished && !serverState.actionCompleted) {
              this.$emit('needsAction', true)
            }
            return
          }
        } catch (e) {
          console.error('Error cargando desde servidor:', e)
        }
      }

      const storeState = this.$store.getters['timer/getManualTimerState'](this.timerId)
      if (storeState) {
        this.applyState(storeState)

        if (storeState.isRunning && Number(storeState.remainingSec) > 0) {
          this.startFromSavedState()
        } else if (!storeState.isRunning && storeState.hasStarted && !storeState.isFinished &&
                  !storeState.actionCompleted && Number(storeState.remainingSec) > 0) {
        } else if (storeState.isFinished && !storeState.actionCompleted) {
          this.$emit('needsAction', true)
        }
        return
      }

      this.remainingSec = this.initialSec
      this.$emit('tick', 0)
    },

    applyState(savedState) {

      this.hasStarted = !!(savedState.hasStarted || savedState.isRunning || (Number(savedState.totalElapsed) > 0))
      this.isRunning = !!savedState.isRunning
      this.isFinished = !!savedState.isFinished
      this.actionCompleted = !!savedState.actionCompleted

      if (savedState.hasStarted && !savedState.isRunning && !savedState.isFinished && !savedState.actionCompleted) {
        this.isRunning = false
      }

      if (savedState.fromServer) {
        if (Number(savedState.remainingSec) === 0 && Number(savedState.totalElapsed) > 0) {
          this.remainingSec = Math.max(0, this.initialSec - Number(savedState.totalElapsed))
        } else {
          this.remainingSec = Number(savedState.remainingSec || this.initialSec)
        }
        this.totalElapsed = Number(savedState.totalElapsed || 0)
      } else {
        this.remainingSec = Number(savedState.remainingSec || this.initialSec)
        this.totalElapsed = Number(savedState.totalElapsed || 0)
      }

      if (this.isRunning && this.remainingSec > 0) {
        const drift = savedState.lastUpdated ? Math.floor((Date.now() - savedState.lastUpdated) / 1000) : 0
        if (drift > 0) {
          this.remainingSec = Math.max(0, this.remainingSec - drift)
          if (this.remainingSec <= 0) {
            return this.handleFinish()
          }
        }
      }

      if (this.actionCompleted) {
        this.isRunning = false
        this.hasStarted = false
        this.isFinished = false
        this.remainingSec = this.initialSec
        this.$emit('needsAction', false)
        this.$emit('tick', 0)
        this.saveState()
        return
      }

      if (this.isFinished) {
        this.$emit('needsAction', true)
        this.$emit('tick', 100)
        return
      }

      const progress = (this.initialSec > 0)
        ? Math.round(((this.initialSec - Math.max(0, this.remainingSec)) / this.initialSec) * 100)
        : 0

      this.$emit('tick', Math.max(0, Math.min(100, progress)))
    },

    startFromSavedState() {

      this.isRunning = true
      this.hasStarted = true
      this.$emit('resumed')
      this.clear()

      this.emitProgress()

      this.intervalId = setInterval(() => {
        if (this.remainingSec > 0) {
          this.remainingSec -= 1
          this.totalElapsed += 1
          this.emitProgress()
          this.saveState()
        }
        if (this.remainingSec <= 0) this.handleFinish()
      }, 1000)

      this.saveState()
    },

    saveState(extra = {}) {
      const stateData = {
        hasStarted: this.hasStarted,
        isRunning: this.isRunning,
        isFinished: this.isFinished,
        actionCompleted: this.actionCompleted,
        remainingSec: this.remainingSec,
        totalElapsed: this.totalElapsed,
        lastUpdated: Date.now(),
        ...extra
      }

      timerSyncService.saveToLocalStorage(this.timerId, stateData)

      this.$store.dispatch('timer/saveManualTimerState', {
        timerId: this.timerId,
        stateData: stateData
      })
    },

    handleContainerClick() {
  if (this.isFinished) return

  // Si está en estado de acción completada, resetear primero
  if (this.actionCompleted) {
    this.actionCompleted = false
    this.isFinished = false
    this.hasStarted = false
    this.remainingSec = this.initialSec
    this.totalElapsed = 0
  }

  // Cambio visual inmediato ANTES de la operación async
  if (!this.hasStarted) {
    this.isRunning = true
    this.hasStarted = true
    this.forceUIUpdate()
    this.start()
    return
  }

  if (this.isRunning) {
    this.isRunning = false
    this.forceUIUpdate()
    this.pause()
    return
  }

  this.isRunning = true
  this.forceUIUpdate()
  this.start()
},

async start() {

  if (this.isRunning) {
    return
  }

  // RESETEAR ESTADO DE ACCIÓN COMPLETADA - PRIMERO
  if (this.actionCompleted) {
    this.actionCompleted = false
    this.isFinished = false
    this.hasStarted = false
    this.remainingSec = this.initialSec
    this.totalElapsed = 0
  }

  // ACTUALIZACIÓN VISUAL INMEDIATA
  this.isRunning = true
  this.hasStarted = true
  this.$emit('started')
  this.forceUIUpdate()

  // Operaciones asíncronas DESPUÉS de la actualización visual
  try {
    if (this.isFinished || this.actionCompleted) {
      this.isRunning = false
      this.hasStarted = false
      this.isFinished = false
      this.actionCompleted = false
      this.remainingSec = this.initialSec
      this.totalElapsed = 0

      await timerSyncService.playTimer(this.timerId, {
        duration: this.initialSec,
        action: 'restart'
      })
    } else if (this.hasStarted) {
      await timerSyncService.playTimer(this.timerId, {
        duration: this.remainingSec,
        action: 'resume',
        elapsed: this.totalElapsed
      })
      this.$emit('resumed')
    } else {
      this.startTime = Date.now()

      await timerSyncService.playTimer(this.timerId, {
        duration: this.initialSec,
        action: 'start'
      })
    }

    // Confirmar estado después de operaciones async
    this.isRunning = true
    this.clear()
    this.emitProgress()

    this.intervalId = setInterval(() => {
      if (this.remainingSec > 0) {
        this.remainingSec -= 1
        this.totalElapsed += 1
        this.emitProgress()
        this.saveState()
      }
      if (this.remainingSec <= 0) {
        this.handleFinish()
      }
    }, 1000)

    this.saveState()

  } catch (error) {
    console.error('Error en start():', error)
    // Revertir estado visual si hay error
    this.isRunning = false
    this.forceUIUpdate()
  }
  this.$emit('stateChanged')
},

    async pause() {
      if (this.isFinished || this.actionCompleted) return

      this.isRunning = false
      this.pausedTime = Date.now()
      this.clear()
      this.$emit('paused')
      this.forceUIUpdate()

      await timerSyncService.pauseTimer(this.timerId)
      this.saveState()
    },

    async stop() {
      if (this.isFinished || this.actionCompleted) return

      this.isRunning = false
      this.clear()
      this.hasStarted = false
      this.isFinished = false
      this.actionCompleted = false
      this.remainingSec = this.initialSec
      this.totalElapsed = 0
      this.$emit('tick', 0)
      this.$emit('stopped')
      this.forceUIUpdate()

      await timerSyncService.stopTimer(this.timerId)
      this.saveState()
    },

    async markAsCompleted() {

      this.actionCompleted = true
      this.isFinished = false
      this.isRunning = false
      this.clear()

      this.remainingSec = this.initialSec
      this.$emit('tick', 0)
      this.$emit('needsAction', false)
      this.forceUIUpdate()

      const result = await timerSyncService.checkTimer(this.timerId)

      if (result && result.success) {
        this.saveState({ completedAt: Date.now() })
        this.$emit('completed')
      } else {
        this.saveState({ completedAt: Date.now() })
        this.$emit('completed')
      }

      this.$emit('stateChanged')
    },

    async reset() {
      this.isRunning = false
      this.hasStarted = false
      this.isFinished = false
      this.actionCompleted = false
      this.remainingSec = this.initialSec
      this.totalElapsed = 0
      this.startTime = null
      this.pausedTime = null
      this.clear()
      this.$emit('tick', 0)
      this.$emit('needsAction', false)
      this.$emit('reset')
      this.forceUIUpdate()

      await timerSyncService.stopTimer(this.timerId)
      timerSyncService.clearState(this.timerId)
      this.$store.dispatch('timer/clearManualTimerState', { timerId: this.timerId })
      this.$emit('stateChanged')
    },

    clear() {
      if (this.intervalId) {
        clearInterval(this.intervalId)
        this.intervalId = null
      }
    },

    emitProgress(forceEnd = false) {
      if (this.initialSec <= 0) {
        this.$emit('tick', 0)
        return
      }
      const elapsed = this.initialSec - Math.max(0, this.remainingSec)
      const pct = forceEnd
        ? 100
        : Math.max(0, Math.min(100, Math.round((elapsed / this.initialSec) * 100)))
      this.$emit('tick', pct)
    },

    async handleFinish() {
      this.isRunning = false
      this.isFinished = true
      this.remainingSec = 0
      this.clear()

      this.emitProgress(true)
      this.saveState()
      this.forceUIUpdate()

      this.$emit('finished')
      this.$emit('needsAction', true)

      await timerSyncService.stopTimer(this.timerId)
    },

    forceUIUpdate() {
      this.uiUpdateKey = Date.now() // Usar timestamp para key única
      this.$nextTick(() => {
        // Doble forzado para asegurar renderizado
        this.$forceUpdate()
        setTimeout(() => {
          this.$forceUpdate()
        }, 10)
      })
    },

    forceSaveState() {
      if (this.hasStarted && !this.actionCompleted) {
        this.saveState()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.countdown-container {
  --icon-size: 18px;
  --gap: 8px;

  position: relative;
  width: 100%;
  height: 100%;
  z-index: 2;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;

  line-height: normal;
}

.icons-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--gap);
}

.icon-btn {
  border: none;
  background: transparent;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  margin-top: 8px;
  transition: transform 0.2s ease;
}
.icon-btn:active { transform: scale(0.96); }

.i-andy-m {
  font-size: 24px !important;
}

.i-andy-m-circle {
  font-size: 18px !important;
}

.circle-bg {
  width: 36px;
  height: 36px;
  margin-top: 4px;

  &--resume {
    background-color: $color-success-100;
  }

  &--stop {
    background-color: $color-error-100;
  }
}

@media (max-width: 1020px) {
  .i-andy-m-circle {
    font-size: 12px !important;
  }
  .circle-bg {
    margin-top: 8px;
    width: 25px;
    height: 25px;
  }
}

.gray-icon   { color: $color-neutral-500; }
.pause-icon  { color: $color-neutral-600; }
.resume-icon { color: $color-success-800; }
.stop-icon   { color: $color-error-500; }

.time-text {
  display: inline-flex;
  align-items: baseline;
  line-height: 28px;
  text-transform: uppercase;

  .hm {
    &.pause { color: $color-black; }
    &.stop  { color: $color-neutral-600; }
    color: $color-neutral-500;
    text-align: center;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px;
    text-transform: uppercase;
  }
  .sec {
    &.pause { color: $color-black; }
    &.stop  { color: $color-neutral-600; }
    color: $color-neutral-500;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 32px;
    text-transform: uppercase;
  }
}

.finished-text {
  display: inline-flex;
  align-items: baseline;
  line-height: 28px;
  text-transform: uppercase;

  .hm.finished {
    color: $color-success-500;
    text-align: center;
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px;
    text-transform: uppercase;
  }
  .sec.finished {
    color: $color-success-500;
    font-size: 14px;
    font-style: normal;
    font-weight: 600;
    line-height: 32px;
    text-transform: uppercase;
  }
}

@media (max-width: 1020px) {
  .time-text {
    .hm {
      font-size: 16px;
    }
    .sec {
      font-size: 10px;
    }
  }
  .finished-text {
    .hm.finished {
      font-size: 16px;
    }
    .sec.finished {
      font-size: 10px;
    }
  }
}

.restart-icon {
  color: $color-neutral-500;
}

.completed-text {
  font-size: 10px;
  color: $color-neutral-500;
  margin-top: 4px;
  text-align: center;
}
</style>
